import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  getBudgetViewType,
  lineItemFilters,
  selectCostOfLaborFilter,
  selectFilteredLineItems,
} from '../../../state/BudgetList/budgetListSlice';
import { EDIT_BUDGET_TABS } from '../../../utils/constants';
import { LineItemWithAmounts } from '../../../utils/customTypes';
import { ParentOfLineItems } from '../../../utils/types/budget';
import {
  buildHierarchyFromLineItemEntities,
  buildLineItemData,
  buildLineItemDataForPrograms,
} from '../helpers/budgetHelper';
import useOptions from './useOptions';
import useProjectLaborOfCost from './useProjectLaborOfCost';
import { BUDGET_TABLE_FILTERS } from 'utils/constants/budget';
import buildNoParentEntity from './buildNoParentEntity';
const useBudgetLineItemCalculations = () => {
  // Selectors
  const selectedBudgetView = useSelector(getBudgetViewType);
  const lineItemsWithoutCostOfLabor = useSelector(selectFilteredLineItems);
  const isCostOfLabor = useSelector(selectCostOfLaborFilter);
  const filters = useSelector(lineItemFilters);

  // hooks
  const { costOfLaborLineItems } = useProjectLaborOfCost();
  const { filteredPrograms, filteredProjects, filteredTeams, filteredVendors } =
    useOptions();

  // State
  const [totals, setTotals] = useState({ totalActual: 0, totalAmount: 0 });
  const [lineItems, setLineItems] = useState<LineItemWithAmounts[]>([]);
  const [lineItemData, setLineItemData] = useState({
    allLineItems: [] as ParentOfLineItems[],
    parentsWithLineItems: [] as ParentOfLineItems[],
  });

  useEffect(() => {
    if (
      isCostOfLabor &&
      selectedBudgetView?.value === EDIT_BUDGET_TABS.PROGRAM_OR_PROJECT
    ) {
      setLineItems([...lineItemsWithoutCostOfLabor, ...costOfLaborLineItems]);
    } else {
      setLineItems(lineItemsWithoutCostOfLabor);
    }
  }, [
    isCostOfLabor,
    lineItemsWithoutCostOfLabor,
    costOfLaborLineItems,
    selectedBudgetView,
  ]);

  // Calculate totals for actuals and amounts
  useEffect(() => {
    const totalActual = lineItems.reduce(
      (sum, lineItem) => sum + lineItem.rawActuals.reduce((a, b) => a + b, 0),
      0
    );

    const totalAmount = lineItems.reduce(
      (sum, lineItem) => sum + lineItem.rawAmounts.reduce((a, b) => a + b, 0),
      0
    );

    setTotals({ totalActual, totalAmount });
  }, [lineItems]);

  // Build line item hierarchy and aggregate data
  useEffect(() => {
    let parents: ParentOfLineItems[] = [];
    let all: ParentOfLineItems[] = [];

    if (selectedBudgetView?.value === EDIT_BUDGET_TABS.PROGRAM_OR_PROJECT) {
      let programs = [...filteredPrograms];
      let projects = [...filteredProjects];
      if (filters && filters[BUDGET_TABLE_FILTERS.PROGRAMS]) {
        const filterValue = filters[BUDGET_TABLE_FILTERS.PROGRAMS];
        programs = filteredPrograms.filter((program) =>
          (filterValue as string[]).includes(program.id)
        );
      }
      if (filters && filters[BUDGET_TABLE_FILTERS.PROJECTS]) {
        const filterValue = filters[BUDGET_TABLE_FILTERS.PROJECTS];
        projects = filteredProjects.filter((project) =>
          (filterValue as string[]).includes(project.id)
        );
      }
      const { rootEntities, allEntities } = buildLineItemDataForPrograms(
        programs,
        projects,
        lineItems
      );
      all = [...allEntities];
      parents = [...rootEntities];

      const noProgram = buildNoParentEntity(
        'No Program/Project',
        'programId',
        all,
        lineItems,
        filteredTeams,
        'projectId'
      );
      all.push(noProgram);
      parents.push(noProgram);
    } else if (selectedBudgetView?.value === EDIT_BUDGET_TABS.VENDORS) {
      let vendors = [...filteredVendors];
      if (filters && filters[BUDGET_TABLE_FILTERS.VENDORS]) {
        const filterValue = filters[BUDGET_TABLE_FILTERS.VENDORS];
        vendors = filteredVendors.filter((vendor) =>
          (filterValue as string[]).includes(vendor.id)
        );
      }
      const { newEntities, entityMap } = buildLineItemData(
        vendors,
        lineItems,
        'vendorId',
        'vendorName'
      );
      all = [...newEntities];
      parents = buildHierarchyFromLineItemEntities(newEntities, entityMap);
      const noVendor = buildNoParentEntity(
        'No Vendor',
        'vendorId',
        all,
        lineItems,
        filteredTeams
      );
      all.push(noVendor);
      parents.push(noVendor);
    } else {
      let teams = [...filteredTeams];
      if (filters && filters[BUDGET_TABLE_FILTERS.TEAMS]) {
        const filterValue = filters[BUDGET_TABLE_FILTERS.TEAMS];
        teams = filteredTeams.filter((team) =>
          (filterValue as string[]).includes(team.id)
        );
      }
      const { newEntities, entityMap } = buildLineItemData(
        teams,
        lineItems,
        'learningTeamId',
        'name'
      );
      all = [...newEntities];
      parents = buildHierarchyFromLineItemEntities(newEntities, entityMap);
      const noTeam = buildNoParentEntity(
        'No Team',
        'learningTeamId',
        all,
        lineItems,
        filteredTeams
      );
      all.push(noTeam);
      parents.push(noTeam);
    }

    setLineItemData({ allLineItems: all, parentsWithLineItems: parents });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    lineItems,
    filteredTeams,
    selectedBudgetView,
    filteredPrograms,
    filteredProjects,
    filteredVendors,
    filters,
  ]);

  const parentsHavingLineItems = useMemo<ParentOfLineItems[]>(() => {
    const hasLineItem = (item: ParentOfLineItems): boolean => {
      let hasLine = !!item.lineItems?.length;

      if (!hasLine && item.children?.length) {
        hasLine = !!item.children?.find((tmp) => hasLineItem(tmp));
      } else if (
        item.lineItems?.length === 1 &&
        item.lineItems[0].description === 'Cost of Labor'
      ) {
        const hasActuals = !!item.lineItems[0].rawActuals.find((val) => !!val);
        const hasAmounts = !!item.lineItems[0].rawAmounts.find((val) => !!val);
        hasLine = !!(hasActuals || hasAmounts);
      }

      return hasLine;
    };

    const items = lineItemData.parentsWithLineItems.map((i) => ({ ...i }));

    // Remove children not having any line item.
    items.forEach((item) => {
      item.children = item.children?.filter((child) => hasLineItem(child));
    });

    const finalItems = items.filter((item) => {
      return hasLineItem(item);
    });

    return finalItems;
  }, [lineItemData.parentsWithLineItems]);

  return { ...lineItemData, ...totals, lineItems, parentsHavingLineItems };
};

export default useBudgetLineItemCalculations;
